import React, { useState, useMemo, useEffect } from "react";
import {
  GoogleMap,
  Marker,
  InfoWindow,
  useLoadScript,
} from "@react-google-maps/api";
import axios from "axios";
import CloseIcon from "@mui/icons-material/Close";
import styles from "../../../styles/location.module.css";
import CircularProgress from "@mui/material/CircularProgress";

import {
  Paper,
  Switch,
  Checkbox,
  IconButton,
  FormControl,
  Select,
  MenuItem,
  ListItemText,
  Chip,
  Box,
  Typography,
  Tooltip,
} from "@mui/material";
const mapContainerStyle = {
  width: "100%",
  height: "450px",
};

const defaultCenter = {
  lat: 20.5937, // Default latitude (center of India)
  lng: 78.9629, // Default longitude (center of India)
};

const libraries = ["places"]; // Include required libraries

const StoreMapping = ({
  CampaignDetails,
  showToggles = false,
  activeProduct,
  setProductType,
  productType = [],
}) => {
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [vendorsList, setVendorsList] = useState([]);
  const [showVendors, setShowVendors] = useState(false);
  const [ipList, setIPList] = useState([]);
  const [showIPs, setShowIPs] = useState(false);
  const [locationsLoading, setLocationsLoading] = useState(true);
  const [storeJobStats, setStoreJobStats] = useState({});

  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAP_API_KEY,
    libraries,
  });

  // Add this helper function to calculate rejected jobs
  const calculateRejectedJobsForStore = (storeKey) => {
    const storeStats = storeJobStats[storeKey];
    if (!storeStats?.jobs) return { rejected: 0, total: 0, allRejected: false };

    const jobsWithServiceTypeZero = storeStats.jobs.filter(
      (j) => j?.serviceType === 0 || j?.serviceType === "0",
    );

    const rejectedJobs = jobsWithServiceTypeZero.filter(
      (j) =>
        j?.approval?.adminStatus === -1 || j?.approval?.clientStatus === -1,
    ).length;

    const totalJobs = jobsWithServiceTypeZero.length;
    const allRejected = totalJobs > 0 && rejectedJobs === totalJobs;

    return {
      rejected: rejectedJobs,
      total: totalJobs,
      allRejected: allRejected,
    };
  };

  // Process job data from sortedStateWiseJobs
  useEffect(() => {
    if (CampaignDetails?.sortedStateWiseJobs) {
      const stats = {};

      CampaignDetails.sortedStateWiseJobs.forEach((job) => {
        const storeKey = `${job.storeName}-${job.storeOwnerPhoneNumber}`;

        if (!stats[storeKey]) {
          stats[storeKey] = {
            storeName: job.storeName,
            storeOwnerName: job.storeOwnerName,
            storeOwnerPhoneNumber: job.storeOwnerPhoneNumber,
            totalJobs: 0,
            rejectedJobs: 0,
            clientStatusCounts: { approved: 0, pending: 0, rejected: 0 },
            adminStatusCounts: { approved: 0, pending: 0, rejected: 0 },
            jobs: [],
          };
        }

        // Calculate if job is rejected
        const isRejected =
          job?.approval?.adminStatus === -1 ||
          job?.approval?.clientStatus === -1;
        const hasServiceTypeZero =
          job?.serviceType === 0 || job?.serviceType === "0";

        stats[storeKey].totalJobs++;

        // Only count jobs with serviceType 0 for total
        if (hasServiceTypeZero) {
          if (isRejected) {
            stats[storeKey].rejectedJobs++;
          }
        }

        stats[storeKey].jobs.push(job);

        // Count client status
        if (job.clientStatus === 1) {
          stats[storeKey].clientStatusCounts.approved++;
        } else if (job.clientStatus === 0) {
          stats[storeKey].clientStatusCounts.pending++;
        } else if (job.clientStatus === -1) {
          stats[storeKey].clientStatusCounts.rejected++;
        }

        // Count admin status
        if (job.adminStatus === 1) {
          stats[storeKey].adminStatusCounts.approved++;
        } else if (job.adminStatus === 0) {
          stats[storeKey].adminStatusCounts.pending++;
        } else if (job.adminStatus === -1) {
          stats[storeKey].adminStatusCounts.rejected++;
        }
      });

      setStoreJobStats(stats);
    }
  }, [CampaignDetails?.sortedStateWiseJobs]);

  // Helper function to normalize coordinates
  const getPosition = (coords) => {
    if (!coords) return null;

    // Handle both {lat, lng} and {latitude, longitude} formats
    if (coords.lat && coords.lng) {
      return { lat: coords.lat, lng: coords.lng };
    }
    if (coords.latitude && coords.longitude) {
      return { lat: coords.latitude, lng: coords.longitude };
    }
    return null;
  };

  // Track loading state for locations
  useEffect(() => {
    setLocationsLoading(true);
    // Simulate async loading if needed, or just wait for CampaignDetails.locations to update
    if (CampaignDetails?.locations && CampaignDetails.locations.length > 0) {
      setLocationsLoading(false);
    } else {
      setLocationsLoading(false); // If you fetch locations asynchronously, set false after fetch
    }
  }, [CampaignDetails?.locations]);

  // Get unique stores
  const uniqueStores = useMemo(() => {
    if (!CampaignDetails?.locations) return [];

    const storeMap = {};

    CampaignDetails.locations.forEach((location) => {
      const storeKey = `${location?.details?.storeName}-${location?.details?.storeOwnerPhoneNumber}`;

      // If first time store found
      if (!storeMap[storeKey]) {
        storeMap[storeKey] = {
          details: location.details,
          locationCoordinates: location.locationCoordinates,
          products: [], // collect all products inside array
          // Add job statistics for this store
          jobStats: storeJobStats[storeKey] || {
            totalJobs: 0,
            rejectedJobs: 0,
            clientStatusCounts: { approved: 0, pending: 0, rejected: 0 },
            adminStatusCounts: { approved: 0, pending: 0, rejected: 0 },
          },
        };
      }

      // Push product inside products array
      if (location.product) {
        storeMap[storeKey].products.push(location.product);
      }
    });

    return Object.values(storeMap);
  }, [CampaignDetails?.locations, storeJobStats]);

  const uniqueLocations = useMemo(() => {
    if (!CampaignDetails?.locations) return [];
    const seenPhoneNumbers = new Set();
    const unique = [];

    CampaignDetails.locations.forEach((location) => {
      unique.push(location);
    });

    return unique;
  }, [CampaignDetails?.locations]);

  const uniqueSites = useMemo(() => {
    if (!CampaignDetails?.locations) return [];
    const seenPhoneNumbers = new Set();
    const unique = [];

    CampaignDetails.locations.forEach((location) => {
      const phoneNumber =
        location?.details?.phoneNumber ||
        location?.details?.storeOwnerPhoneNumber;

      if (!phoneNumber) {
        return;
      }

      if (!seenPhoneNumbers.has(phoneNumber)) {
        seenPhoneNumbers.add(phoneNumber);
        unique.push(location);
      }
    });

    return unique;
  }, [CampaignDetails?.locations]);

  const uniqueProductTypes = useMemo(() => {
    if (!CampaignDetails?.locations) return [];
    const types = CampaignDetails.locations
      .filter((loc) => loc.product?.productType) // only keep ones with productType
      .map((loc) => loc.product.productType);

    return ["All", ...Array.from(new Set(types))];
  }, [CampaignDetails?.locations]);

  const filteredLocations = useMemo(() => {
    if (!uniqueLocations) return [];

    const selectedTypes = Array.isArray(productType) ? productType : [];

    // If nothing selected → show all
    if (selectedTypes?.length === 0 || selectedTypes?.includes("All")) {
      return uniqueLocations;
    }

    // Filter based on product type
    return uniqueLocations.filter((loc) =>
      selectedTypes?.includes(loc.product?.productType),
    );
  }, [uniqueLocations, productType]);

  // Get filtered unique stores based on product type
  const filteredUniqueStores = useMemo(() => {
    if (!uniqueStores) return [];

    const selectedTypes = Array.isArray(productType) ? productType : [];

    // if no filter, return all
    if (selectedTypes.length === 0 || selectedTypes.includes("All")) {
      return uniqueStores;
    }

    // Return only stores where ANY product matches chosen type
    return uniqueStores.filter((store) =>
      store.products.some((p) => selectedTypes.includes(p.productType)),
    );
  }, [uniqueStores, productType]);

  // Calculate center position using unique locations
  const getCenterPosition = () => {
    const source = filteredLocations?.length
      ? filteredLocations
      : uniqueLocations;
    if (!source?.length) return defaultCenter;

    const position = getPosition(source[0].locationCoordinates);
    return position || defaultCenter;
  };
  const [mapCenter, setMapCenter] = useState(defaultCenter);
  useEffect(() => {
    if (filteredLocations?.length > 0) {
      const pos = getPosition(filteredLocations[0].locationCoordinates);
      if (pos) setMapCenter(pos);
    }
  }, [filteredLocations]);

  const markerLocations = useMemo(() => {
    const storeMap = new Map();

    filteredLocations.forEach((location) => {
      const storeKey = `${location?.details?.storeName}-${location?.details?.storeOwnerPhoneNumber}`;

      if (!storeMap.has(storeKey)) {
        storeMap.set(storeKey, location);
      }
    });

    return Array.from(storeMap.values());
  }, [filteredLocations]);

  if (loadError) return <div>Error loading maps</div>;

  if (
    !isLoaded ||
    locationsLoading ||
    !CampaignDetails?.locations ||
    !CampaignDetails?.locations.length
  ) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
        <span style={{ marginLeft: 16 }}>Loading map and locations...</span>
      </div>
    );
  }

  // Validate locations data
  const hasValidLocations = CampaignDetails?.locations?.some((location) => {
    const coords = location?.locationCoordinates;
    return (
      (coords?.lat && coords?.lng) || (coords?.latitude && coords?.longitude)
    );
  });

  if (!hasValidLocations) {
    return (
      <div className="text-center py-4 text-gray-500">
        No location data available.
      </div>
    );
  }

  return (
    <div className="bg-white shadow-md rounded-lg p-4">
      <div className="flex justify-between item-center">
        <h2 className="text-lg font-semibold text-gray-800">{`Site Mapping: ${filteredUniqueStores?.length}`}</h2>
      </div>
      <div className="mt-4">
        <GoogleMap
          mapContainerStyle={mapContainerStyle}
          zoom={10}
          center={mapCenter} // <--- FIXED center value
          options={{
            mapTypeControl: false,
            streetViewControl: false,
          }}
        >
          {markerLocations?.map((location, index) => {
            const position = getPosition(location.locationCoordinates);

            if (!position) {
              console.warn("Invalid coordinates for location:", location);
              return null;
            }

            const storeKey = `${location.details?.storeName}-${location.details?.storeOwnerPhoneNumber}`;
            const rejectionStatus = calculateRejectedJobsForStore(storeKey);

            return (
              <Marker
                key={`location-${index}-${
                  location.details?.phoneNumber ||
                  location.details?.storeOwnerPhoneNumber
                }`}
                position={position}
                onClick={() => setSelectedLocation(location)}
                icon={{
                  path: "M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z",
                  fillColor: rejectionStatus.allRejected ? "#000" : "#ff0000",
                  fillOpacity: 1,
                  strokeColor: "#FFFFFF",
                  strokeWeight: 1,
                  scale: 2,
                  anchor: new window.google.maps.Point(12, 24),
                }}
              />
            );
          })}

          {showVendors &&
            vendorsList?.map((vendor, index) => {
              const position = getPosition(vendor.locationCoordinates);
              if (!position) {
                console.warn("Invalid coordinates for vendor:", vendor);
                return null;
              }

              return (
                <Marker
                  key={`vendor-${index}`}
                  position={position}
                  icon={{
                    url: "http://maps.google.com/mapfiles/ms/icons/blue-dot.png",
                  }}
                />
              );
            })}

          {showIPs &&
            ipList?.map((ip, index) => {
              const position = getPosition(ip.locationCoordinates);
              if (!position) {
                console.warn("Invalid coordinates for IP:", ip);
                return null;
              }

              return (
                <Marker
                  key={`ip-${index}`}
                  position={position}
                  icon={{
                    url: "http://maps.google.com/mapfiles/ms/icons/green-dot.png",
                  }}
                />
              );
            })}

          {/* InfoWindow for selected location */}
          {selectedLocation && (
            <InfoWindow
              position={{
                lat:
                  getPosition(selectedLocation.locationCoordinates).lat +
                  0.0000005,
                lng: getPosition(selectedLocation.locationCoordinates).lng,
              }}
              onCloseClick={() => setSelectedLocation(null)} // Close when clicking outside
            >
              <Paper
                elevation={1}
                sx={{
                  width: 200,
                  borderRadius: 0,
                  margin: 0,
                  padding: 0,
                  overflow: "hidden",
                  border: "none",
                }}
              >
                {/* Header Section with Close Button */}
                <div className={styles.locationHeader}>
                  <h4 className={styles.storeName}>
                    {selectedLocation.details?.storeName}
                  </h4>

                  {/* Close Button */}
                  <IconButton
                    size="small"
                    onClick={() => setSelectedLocation(null)}
                    className={styles.closeButton}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </div>

                {/* Details Section */}
                <div className={styles.detailsContainer}>
                  <div className={styles.detailsLabel}>
                    <p>Owner:</p>
                    <p>Contact:</p>
                    <p>Address:</p>
                  </div>

                  <div className={styles.detailsValue}>
                    <p>{selectedLocation?.details?.storeOwnerName}</p>
                    <p>{selectedLocation?.details?.storeOwnerPhoneNumber}</p>
                    <p className={styles.address}>
                      {`${selectedLocation?.details?.address}, ${selectedLocation?.details?.city}, ${selectedLocation?.details?.state}, ${selectedLocation?.details?.zipCode}`}
                    </p>
                  </div>
                </div>

                {/* Job Status Section */}
                <Box sx={{ p: 2, pt: 1, borderTop: "1px solid #eee" }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Job Status Summary
                  </Typography>

                  {/* Rejected Jobs Count*/}
                  <Box
                    sx={{ mb: 2, p: 1.5, bgcolor: "#f5f5f5", borderRadius: 1 }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <Typography variant="body2">Rejected Jobs:</Typography>
                      <Tooltip
                        title={`${
                          calculateRejectedJobsForStore(
                            `${selectedLocation.details?.storeName}-${selectedLocation.details?.storeOwnerPhoneNumber}`,
                          ).rejected
                        } rejected out of ${
                          calculateRejectedJobsForStore(
                            `${selectedLocation.details?.storeName}-${selectedLocation.details?.storeOwnerPhoneNumber}`,
                          ).total
                        }`}
                        arrow
                      >
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <Chip
                            size="small"
                            label={
                              calculateRejectedJobsForStore(
                                `${selectedLocation.details?.storeName}-${selectedLocation.details?.storeOwnerPhoneNumber}`,
                              ).rejected
                            }
                            color="error"
                            sx={{ fontWeight: "medium" }}
                          />
                          <Typography variant="body2" color="text.secondary">
                            /
                          </Typography>
                          <Typography variant="body2">
                            {
                              calculateRejectedJobsForStore(
                                `${selectedLocation.details?.storeName}-${selectedLocation.details?.storeOwnerPhoneNumber}`,
                              ).total
                            }
                          </Typography>
                        </Box>
                      </Tooltip>
                    </Box>

                    {/* Show warning if all jobs are rejected */}
                    {calculateRejectedJobsForStore(
                      `${selectedLocation.details?.storeName}-${selectedLocation.details?.storeOwnerPhoneNumber}`,
                    ).allRejected && (
                      <Box
                        sx={{
                          mt: 1,
                          p: 1,
                          bgcolor: "#ffebee",
                          borderRadius: 1,
                        }}
                      >
                        <Typography
                          variant="caption"
                          color="error"
                          sx={{ display: "flex", alignItems: "center" }}
                        >
                          ⚠️ All jobs for this store are rejected
                        </Typography>
                      </Box>
                    )}
                  </Box>

                  {/* Rest of existing client/admin status chips */}
                  <Box sx={{ mb: 2 }}>
                    <Box
                      sx={{
                        display: "flex",
                        gap: 1,
                        flexWrap: "wrap",
                        mt: 0.5,
                      }}
                    >
                      {storeJobStats[
                        `${selectedLocation.details?.storeName}-${selectedLocation.details?.storeOwnerPhoneNumber}`
                      ]?.clientStatusCounts && (
                        <>
                          {storeJobStats[
                            `${selectedLocation.details?.storeName}-${selectedLocation.details?.storeOwnerPhoneNumber}`
                          ].clientStatusCounts.approved > 0 && (
                            <Chip
                              size="small"
                              label={`Approved: ${
                                storeJobStats[
                                  `${selectedLocation.details?.storeName}-${selectedLocation.details?.storeOwnerPhoneNumber}`
                                ].clientStatusCounts.approved
                              }`}
                              color="success"
                              variant="outlined"
                            />
                          )}
                          {storeJobStats[
                            `${selectedLocation.details?.storeName}-${selectedLocation.details?.storeOwnerPhoneNumber}`
                          ].clientStatusCounts.pending > 0 && (
                            <Chip
                              size="small"
                              label={`Pending: ${
                                storeJobStats[
                                  `${selectedLocation.details?.storeName}-${selectedLocation.details?.storeOwnerPhoneNumber}`
                                ].clientStatusCounts.pending
                              }`}
                              color="warning"
                              variant="outlined"
                            />
                          )}
                          {storeJobStats[
                            `${selectedLocation.details?.storeName}-${selectedLocation.details?.storeOwnerPhoneNumber}`
                          ].clientStatusCounts.rejected > 0 && (
                            <Chip
                              size="small"
                              label={`Rejected: ${
                                storeJobStats[
                                  `${selectedLocation.details?.storeName}-${selectedLocation.details?.storeOwnerPhoneNumber}`
                                ].clientStatusCounts.rejected
                              }`}
                              color="error"
                              variant="outlined"
                            />
                          )}
                        </>
                      )}
                    </Box>
                  </Box>

                  <Box>
                    <Box
                      sx={{
                        display: "flex",
                        gap: 1,
                        flexWrap: "wrap",
                        mt: 0.5,
                      }}
                    >
                      {storeJobStats[
                        `${selectedLocation.details?.storeName}-${selectedLocation.details?.storeOwnerPhoneNumber}`
                      ]?.adminStatusCounts && (
                        <>
                          {storeJobStats[
                            `${selectedLocation.details?.storeName}-${selectedLocation.details?.storeOwnerPhoneNumber}`
                          ].adminStatusCounts.approved > 0 && (
                            <Chip
                              size="small"
                              label={`Approved: ${
                                storeJobStats[
                                  `${selectedLocation.details?.storeName}-${selectedLocation.details?.storeOwnerPhoneNumber}`
                                ].adminStatusCounts.approved
                              }`}
                              color="success"
                              variant="outlined"
                            />
                          )}
                          {storeJobStats[
                            `${selectedLocation.details?.storeName}-${selectedLocation.details?.storeOwnerPhoneNumber}`
                          ].adminStatusCounts.pending > 0 && (
                            <Chip
                              size="small"
                              label={`Pending: ${
                                storeJobStats[
                                  `${selectedLocation.details?.storeName}-${selectedLocation.details?.storeOwnerPhoneNumber}`
                                ].adminStatusCounts.pending
                              }`}
                              color="warning"
                              variant="outlined"
                            />
                          )}
                          {storeJobStats[
                            `${selectedLocation.details?.storeName}-${selectedLocation.details?.storeOwnerPhoneNumber}`
                          ].adminStatusCounts.rejected > 0 && (
                            <Chip
                              size="small"
                              label={`Rejected: ${
                                storeJobStats[
                                  `${selectedLocation.details?.storeName}-${selectedLocation.details?.storeOwnerPhoneNumber}`
                                ].adminStatusCounts.rejected
                              }`}
                              color="error"
                              variant="outlined"
                            />
                          )}
                        </>
                      )}
                    </Box>
                  </Box>
                </Box>
              </Paper>
            </InfoWindow>
          )}
        </GoogleMap>
        <style>
          {`
          .gm-ui-hover-effect {
            display: none !important; /* Hide the close button */
          }
          .gm-style-iw-d {
            padding: 0 !important; /* Remove padding from the InfoWindow content */
          }
        `}
        </style>
      </div>
      {showToggles && (
        <>
          <div>
            <span>{`Vendors`}</span>
            <Switch
              onChange={async () => {
                if (!showVendors) {
                  let totalVendors = await getVendorList();
                  setVendorsList(totalVendors);
                  setShowVendors(true);
                } else {
                  setShowVendors(false);
                }
              }}
            />
          </div>
          <div>
            <span>{`IPs`}</span>
            <Switch
              onChange={async () => {
                if (!showIPs) {
                  let totalIPs = await fetchIps();
                  setIPList(totalIPs);
                  setShowIPs(true);
                } else {
                  setShowIPs(false);
                }
              }}
            />
          </div>
        </>
      )}
    </div>
  );
};

// API functions remain the same

const getVendorList = async () => {
  try {
    const token = localStorage.getItem("token");
    const response = await axios.get(
      `${process.env.REACT_APP_URL}/api/vendorsForMap`,
      {
        headers: { Authorization: `Bearer ${token}` },
      },
    );
    if (response?.data?.success) {
      return response?.data?.data;
    }
  } catch (error) {
    console.error("Error fetching vendors:", error);
    return [];
  }
};

const fetchIps = async () => {
  try {
    const token = localStorage.getItem("token");
    const response = await axios.get(
      `${process.env.REACT_APP_URL}/installationPartner/view`,
      {
        headers: { Authorization: `Bearer ${token}` },
      },
    );

    if (response?.data?.success) {
      return response?.data?.data;
    }
  } catch (error) {
    console.error("Error fetching filtered vendors:", error);
    return [];
  } finally {
  }
};
export default StoreMapping;
